import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, Tag, Spin, Empty, message, Button } from 'antd'
import styled from 'styled-components'
import { getAssignments } from '../../services/assignmentActivityAPI'

const T = {
  primary: '#3249D7', textPrimary: '#242E39', textSecondary: '#637487',
  green: '#4cb187', red: '#E5484D', amber: '#B5641E',
}
const Wrap = styled.div`padding: 24px; max-width: 1180px; margin: 0 auto;`
const TitleRow = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px;`
const Title = styled.h2`font-size: 20px; font-weight: 700; color: ${T.textPrimary}; margin: 0;`

const stateColor = (s) =>
  s === 'completed' ? T.green
    : s === 'completed_with_errors' ? T.amber
    : s === 'failed' ? T.red : T.primary

const TERMINAL = ['completed', 'completed_with_errors', 'failed']
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—')

/**
 * Assignment Activity index — a persistent list of recent assignment jobs so the
 * activity view is reachable any time (not just from the post-assign popup / email).
 * Live-polls while any job is still running. Row → that job's activity page.
 */
const AssignmentActivityList = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [jobs, setJobs] = useState([])

  const load = useCallback(async () => {
    try {
      const res = await getAssignments({ limit: 100 })
      setJobs(res.data?.data?.jobs || [])
    } catch (err) {
      message.error('Failed to load assignment activity')
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Live-refresh while any listed job is still in progress.
  const anyRunning = jobs.some((j) => !TERMINAL.includes(j.state))
  useEffect(() => {
    if (!anyRunning) return undefined
    const id = setInterval(load, 3000)
    return () => clearInterval(id)
  }, [anyRunning, load])

  const columns = [
    { title: 'TYPE', dataIndex: 'assessmentType', key: 'type',
      render: (v) => <span style={{ fontWeight: 600, color: T.textPrimary }}>{v}</span> },
    { title: 'ENTITY', dataIndex: 'entityType', key: 'entity', render: (v) => <Tag>{v}</Tag> },
    { title: 'TOTAL', dataIndex: 'total', key: 'total', width: 80 },
    { title: 'ASSIGNED', dataIndex: 'assigned', key: 'assigned', width: 100 },
    { title: 'NOTIFIED', dataIndex: 'emailed', key: 'emailed', width: 100,
      render: (v) => <span style={{ color: T.green }}>{v}</span> },
    { title: 'FAILED', dataIndex: 'failed', key: 'failed', width: 90,
      render: (v) => <span style={{ color: v ? T.red : T.textSecondary }}>{v}</span> },
    { title: 'STATE', dataIndex: 'state', key: 'state',
      render: (s) => <Tag color={stateColor(s)}>{(s || '').replace(/_/g, ' ')}</Tag> },
    { title: 'CREATED', dataIndex: 'createdAt', key: 'createdAt',
      render: (d) => <span style={{ color: T.textSecondary, fontSize: 12 }}>{fmt(d)}</span> },
  ]

  if (loading) return <Spin style={{ display: 'block', margin: '60px auto' }} />

  return (
    <Wrap>
      <TitleRow>
        <Title>Assignment activity</Title>
        <Button onClick={load}>Refresh</Button>
      </TitleRow>
      {jobs.length === 0
        ? <Empty description="No assignment jobs yet" />
        : <Table
            rowKey="id"
            columns={columns}
            dataSource={jobs}
            size="middle"
            onRow={(r) => ({ onClick: () => navigate(`/assessment/activity/job/${r.id}`, { state: { from: '/assessment/activity' } }), style: { cursor: 'pointer' } })}
            pagination={{ pageSize: 25, hideOnSinglePage: true }}
          />}
    </Wrap>
  )
}

export default AssignmentActivityList
